import * as React from 'react';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, ICruiseReducer } from '../../interfaces';
import { CabinDetailsPriceSection } from './CabinDetailsPriceSection';
import { CabinDescription } from './CabinDescription';
import { CabinValueComponent, CabinOptionComponent } from './CabinDetailsCustomSelect';

interface ICabinDetails {
    cruisePage: ICruiseReducer;
    discountThreshold?: number;
    cabin: ICabinInfo;
    cabins: ICabinInfo[];
    urlBase: string;
    isPriceShown?: boolean;
    isCabinSelectShown?: boolean;
    numberOfPassengers?: number;
    onClose: () => void;
}

import {
    Dialog,
    DialogHeader,
    DialogHeaderTitle,
    DialogContent,
    DialogFooter,
} from '../Dialog/Dialog';
import loadable from '@loadable/component';
import styled from '@emotion/styled';
import { ImgWithFallback } from '../ImgWithFallback';
import { BreakpointsEnum } from '../../helpers/breakpoints';

const CustomSelect = loadable(() => import('../CustomSelect/CustomSelect'));

const ImgWithFallbackStyled = styled(ImgWithFallback)`
    width: 100%;
    margin-top: 8px;
    border-radius: ${({ theme }) => theme.container.borderRadius};
`;

const CabinInfo = styled.div`
    display: flex;
    column-gap: 24px;
`;

const CabinInfoDescription = styled.div`
    width: 100%;
    @media (min-width: ${BreakpointsEnum.sm}) {
        width: 50%;
        ${ImgWithFallbackStyled} {
            display: none;
        }
    }
`;
const CabinInfoImage = styled.div`
    width: 100%;
    display: none;
    @media (min-width: ${BreakpointsEnum.sm}) {
        width: 50%;
        display: block;
        ${ImgWithFallbackStyled} {
            display: block;
        }
    }
`;

const CabinInfoTitle = styled.h4`
    color: ${({ theme }) => theme.text.color.primary};
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
    @media (min-width: ${BreakpointsEnum.sm}) {
        font-size: 20px;
        line-height: 32px;
    }
`;

const CabinInfoSubText = styled.p`
    color: ${({ theme }) => theme.text.color.primary2};
    font-size: 14px;
    line-height: 24px;
`;

const CabinDescriptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    margin: 24px 0;
`;

const CabinDetailsText = styled.p`
    color: ${({ theme }) => theme.text.color.primary};
    font-size: 14px;
    line-height: 22px;
`;

const CustomSelectStyled = styled(CustomSelect)`
    margin-top: 24px;
    .Select.direction-down.is-open {
        box-shadow: none;
        max-width: none;
        .Select-menu-outer {
            max-width: none;
            border: 1px solid ${({ theme }) => theme.colors.iron[100]};
            border-radius: ${({ theme }) => theme.container.borderRadius};
            box-shadow: 0 6px 8px rgba(0, 0, 0, 0.02);
        }
    }
    .Select-control {
        padding: 10px 0;
        border: 2px solid ${({ theme }) => theme.colors.iron[100]};
        border-radius: ${({ theme }) => theme.container.borderRadius};
        .Select-multi-value-wrapper {
            width: calc(100% - 32px);
        }
    }
    .Select-arrow-zone {
        padding: 0;
    }
`;

export const CabinDetailsDialog = (props: ICabinDetails) => {
    const {
        urlBase,
        isPriceShown,
        isCabinSelectShown,
        numberOfPassengers,
        cabins,
        cabin,
        discountThreshold,
        cruisePage,
    } = props;
    const { sails, selectedSailIndex, soldOutBooking } = cruisePage;
    const initialSelectedCabinIndex = isPriceShown ? cabin?.cabinType.nid : 0;
    const cabinContentRef = React.useRef(null);
    const [selectedCabinIndex, setSelectedCabinIndex] = React.useState(initialSelectedCabinIndex);

    // If users switch cabins after scrolling through the dialog content,
    // the content should automatically scroll to the top
    React.useEffect(() => {
        if (cabinContentRef.current) {
            cabinContentRef.current.scrollTop = 0;
        }
    }, [selectedCabinIndex]);

    const getSelectedCabin = () => {
        return cabins.find((item) => item.cabinType.nid === selectedCabinIndex) || cabins[0];
    };

    const getLabel = (cabin) => {
        if (!cabin) return '';

        return cabin.cabinType.labels.labelForRequestForm || cabin.cabinType.labels.withoutPrice;
    };

    const handleCabinChange = (index: number) => setSelectedCabinIndex(index);

    const renderSelect = () => {
        const selectedCabin = getSelectedCabin();

        const options = cabins.reduce((acc, cabin) => {
            const cabinTypeId = cabin.cabinType.nid;
            const cabinLabel = getLabel(cabin);

            // de-duplication
            if (acc.some((item) => item.label === cabinLabel || item.id === cabinTypeId))
                return acc;

            return [
                ...acc,
                {
                    id: cabinTypeId,
                    label: getLabel(cabin),
                    value: getLabel(cabin),
                    maxDiscount: cabin.discountPercentage || undefined, // Do not show 0
                },
            ];
        }, []);

        const label = getLabel(selectedCabin);
        const value = { label, value: label, id: selectedCabin?.cabinType.nid };

        return (
            <CustomSelectStyled
                value={value}
                options={options}
                valueComponent={CabinValueComponent}
                optionComponent={CabinOptionComponent}
                onChange={handleCabinChange}
                clearable={false}
                searchable={false}
            />
        );
    };

    const selectedCabin = getSelectedCabin();

    if (!selectedCabin) return null;

    const currentCabin = selectedCabin?.cabinType;
    const url = `${urlBase}/${currentCabin?.nid}`;
    const cabinText = __(
        'The price of the chosen cabin depends on your cabin location, the equipment criteria and the size.',
        'dreamlines',
    );

    return (
        <Dialog open onClose={props.onClose}>
            <DialogHeader>
                <DialogHeaderTitle>{__('Cabin details', 'dreamlines')}</DialogHeaderTitle>
                {isCabinSelectShown && renderSelect()}
            </DialogHeader>
            <DialogContent ref={cabinContentRef}>
                <CabinInfo>
                    <CabinInfoDescription>
                        <CabinInfoTitle>{currentCabin.title}</CabinInfoTitle>
                        <CabinInfoSubText>{cabinText}</CabinInfoSubText>
                        <ImgWithFallbackStyled
                            imageFallback="cabin"
                            imageUrl={currentCabin.image.original}
                            alt={currentCabin.title}
                        />
                        <CabinDescriptionsWrapper>
                            <CabinDescription
                                title="Location"
                                descriptionText={currentCabin.location}
                            />
                            <CabinDescription
                                title="Occupancy"
                                descriptionText={currentCabin?.maxPassengers.toString()}
                            />
                            <CabinDescription
                                title="Cabin size"
                                descriptionText={currentCabin.size}
                            />
                            <CabinDescription title="Bed" descriptionText={currentCabin.bed} />
                        </CabinDescriptionsWrapper>
                        {currentCabin.isGuaranteeCabin && (
                            <CabinDetailsText>{currentCabin.guaranteeCabinInfo}</CabinDetailsText>
                        )}
                        <CabinDescription
                            title="Amenities"
                            descriptionText={currentCabin.amenities}
                        />
                        <CabinDescription
                            title="Following advantages are included"
                            descriptionText={currentCabin.advantages}
                        />
                    </CabinInfoDescription>
                    <CabinInfoImage>
                        <ImgWithFallbackStyled
                            imageFallback="cabin"
                            imageUrl={currentCabin.image.original}
                            alt={currentCabin.title}
                        />
                    </CabinInfoImage>
                </CabinInfo>
            </DialogContent>
            {isPriceShown && (
                <DialogFooter>
                    <CabinDetailsPriceSection
                        url={url}
                        currency={selectedCabin.currency}
                        perPersonPrice={selectedCabin.perPersonPrice}
                        discountPercentage={selectedCabin.discountPercentage}
                        isButtonShown={isPriceShown && isCabinSelectShown}
                        departure={sails.length > 0 ? sails[selectedSailIndex].departure : null}
                        soldOutBooking={soldOutBooking}
                        discountThreshold={discountThreshold}
                        cabin={selectedCabin}
                        numberOfPassengers={numberOfPassengers}
                    />
                </DialogFooter>
            )}
        </Dialog>
    );
};
